import {
  Document, Packer, Paragraph, TextRun, AlignmentType,
  Table, TableRow, TableCell, WidthType, VerticalAlign, BorderStyle, HeightRule,
} from 'docx';

const BODY_FONT = { ascii: 'MS Mincho', eastAsia: 'MS Mincho', hAnsi: 'MS Mincho' };

const THIN = { style: BorderStyle.SINGLE, size: 4, color: '000000' };
const NONE = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const TABLE_BORDERS = { top: THIN, bottom: THIN, left: THIN, right: THIN, insideHorizontal: THIN, insideVertical: THIN };
const VALUE_SIZE = 22; // 項目の値・本文は11pt。ラベルは既定値(9pt)のまま。

// 実物の「復命書」様式（研修参加報告）に合わせた6列のグリッド幅（DXA、合計9952）。
const WIDTHS = [1123, 4009, 1134, 1276, 1276, 1134];

function labelCell(text, width, opts = {}) {
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    columnSpan: opts.columnSpan,
    rowSpan: opts.rowSpan,
    borders: opts.borders,
    margins: { top: 60, bottom: 60, left: 100, right: 100 },
    verticalAlign: VerticalAlign.CENTER,
    children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text, size: opts.size })] })],
  });
}
function valueCell(lines, width, opts = {}) {
  const arr = (Array.isArray(lines) ? lines : [lines]).filter((l) => l);
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    columnSpan: opts.columnSpan,
    rowSpan: opts.rowSpan,
    borders: opts.borders,
    margins: { top: 60, bottom: 60, left: 100, right: 100 },
    verticalAlign: VerticalAlign.CENTER,
    children: arr.length ? arr.map((t) => new Paragraph({ children: [new TextRun({ text: t, size: VALUE_SIZE })] }))
      : [new Paragraph({ children: [new TextRun({ text: '', size: VALUE_SIZE })] })],
  });
}

/** タイトル行＋承認印欄（施設長／総務部長／部署責任者）。原本どおりタイトルと余白列の間の罫線は消す。 */
function titleRow() {
  const wLeft = WIDTHS[0] + WIDTHS[1];
  return [
    new TableRow({
      // ラベル行は少し狭く、下の印鑑を押す行を少し広く（見た目のバランス調整）。
      height: { value: 450, rule: HeightRule.AT_LEAST },
      children: [
        new TableCell({
          width: { size: wLeft, type: WidthType.DXA },
          columnSpan: 2,
          rowSpan: 2,
          borders: { right: NONE },
          verticalAlign: VerticalAlign.CENTER,
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text: '　　復　命　書', bold: true, size: 44 })],
          })],
        }),
        new TableCell({
          width: { size: WIDTHS[2], type: WidthType.DXA },
          rowSpan: 2,
          borders: { left: NONE },
          children: [new Paragraph({ text: '' })],
        }),
        labelCell('施設長', WIDTHS[3]),
        labelCell('総務部長', WIDTHS[4]),
        labelCell('部署責任者', WIDTHS[5]),
      ],
    }),
    new TableRow({
      height: { value: 900, rule: HeightRule.AT_LEAST },
      children: [
        valueCell('', WIDTHS[3]),
        valueCell('', WIDTHS[4]),
        valueCell('', WIDTHS[5]),
      ],
    }),
  ];
}

function fieldRow(l1, v1, l2, v2) {
  const wRight = WIDTHS[3] + WIDTHS[4] + WIDTHS[5];
  return new TableRow({
    height: { value: 550, rule: HeightRule.AT_LEAST },
    children: [
      labelCell(l1, WIDTHS[0], { size: VALUE_SIZE }),
      valueCell(v1, WIDTHS[1]),
      labelCell(l2, WIDTHS[2], { size: VALUE_SIZE }),
      valueCell(v2, wRight, { columnSpan: 3 }),
    ],
  });
}

/** 本文欄。罫線などの装飾はなく、記入欄として十分な高さを確保した1つのセル。 */
function bodyRow(bodyText) {
  const lines = (bodyText || '').split('\n');
  const totalWidth = WIDTHS.reduce((a, b) => a + b, 0);
  return new TableRow({
    height: { value: 8000, rule: HeightRule.AT_LEAST },
    children: [
      new TableCell({
        width: { size: totalWidth, type: WidthType.DXA },
        columnSpan: WIDTHS.length,
        margins: { top: 160, bottom: 160, left: 140, right: 140 },
        children: lines.map((l) => new Paragraph({ children: [new TextRun({ text: l, size: VALUE_SIZE })] })),
      }),
    ],
  });
}

/**
 * 復命書（研修参加報告）をWordファイル(Blob)にする。
 * fields: { trainingName, trainingPlace, datetime, submitDate, jobType, name }, bodyText: 本文
 */
export async function buildFukumeishoWordBlob(fields, bodyText) {
  const totalWidth = WIDTHS.reduce((a, b) => a + b, 0);
  const doc = new Document({
    styles: {
      default: {
        // 個別にsizeを指定していないTextRunに効く既定値。タイトル「復命書」だけ
        // 明示的にsize指定(44)しているため対象外＝9ptにならず現状のまま残る。
        document: { run: { font: BODY_FONT, size: 18 } },
      },
    },
    sections: [{
      properties: {
        // docxのデフォルト余白(上下左右1440DXA=1インチ)だと印刷可能幅が9026DXAしかなく、
        // 表の幅(9952DXA)より狭いため右側が印刷範囲からはみ出す。原本の実際のページ設定
        // （A4・左右の余白を詰めた値）に合わせ、印刷可能幅を10091DXAまで広げて収める。
        page: {
          size: { width: 11906, height: 16838 }, // A4
          margin: { top: 720, right: 851, bottom: 720, left: 964, header: 851, footer: 992, gutter: 0 },
        },
      },
      children: [
        new Table({
          width: { size: totalWidth, type: WidthType.DXA },
          columnWidths: WIDTHS,
          borders: TABLE_BORDERS,
          rows: [
            ...titleRow(),
            fieldRow('研修名', fields.trainingName, '提出日', fields.submitDate),
            fieldRow('研修場所', fields.trainingPlace, '職種', fields.jobType),
            fieldRow('日時', fields.datetime, '氏名', fields.name),
            bodyRow(bodyText),
          ],
        }),
      ],
    }],
  });
  return Packer.toBlob(doc);
}
